import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, EMPTY, of } from 'rxjs';
import { catchError, defaultIfEmpty, finalize, map, tap } from 'rxjs/operators';
import {
  CompleteEmailVerificationRequest,
  CompleteEmailVerificationResponse,
  CreateAccountRequest,
  JoinAccountRequest,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  RequestPasswordResetRequest,
  RequestPasswordResetResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  User
} from '../models/auth.model';
import { AuthStoreService } from './auth-store.service';
import { AuthApiService } from './auth-api.service';
import { AuthProfileMapper } from './auth-profile.mapper';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(
    private readonly authApi: AuthApiService,
    private readonly router: Router,
    private readonly authStore: AuthStoreService,
    private readonly profileMapper: AuthProfileMapper
  ) {}

  get currentUser(): User | null {
    return this.authStore.currentUser;
  }

  get token(): string | null {
    return this.authStore.token;
  }

  register(request: RegisterRequest): Observable<RegisterResponse> {
    return this.authApi.register(request);
  }

  requestPasswordReset(request: RequestPasswordResetRequest): Observable<RequestPasswordResetResponse> {
    return this.authApi.requestPasswordReset(request);
  }

  completeEmailVerification(request: CompleteEmailVerificationRequest): Observable<CompleteEmailVerificationResponse> {
    return this.authApi.completeEmailVerification(request).pipe(
      tap((response) => this.handleAuthSideEffects('email verification', response.token, response.refreshToken))
    );
  }

  resetPassword(request: ResetPasswordRequest): Observable<ResetPasswordResponse> {
    return this.authApi.resetPassword(request).pipe(
      tap((response) => this.handleAuthSideEffects('password reset', response.token, response.refreshToken))
    );
  }

  login(request: LoginRequest): Observable<LoginResponse> {
    return this.authApi.login(request).pipe(
      tap((response) => this.handleAuthSideEffects('login', response.token, response.refreshToken))
    );
  }

  logout(): void {
    this.authStore.clearAuthState();
    this.router.navigate(['/login']).catch(() => undefined);
  }

  loadProfile(): Observable<User> {
    const lockApplication = !this.authStore.hasCompletedInitialLoad;
    if (lockApplication) {
      this.authStore.setProfileLoading(true);
    }

    return this.authApi.fetchProfile().pipe(
      map((response) => {
        const user = this.profileMapper.map(response);
        this.authStore.setCurrentUser(user);
        this.authStore.markProfileLoaded();
        return user;
      }),
      catchError((error) => {
        console.error('Failed to load user profile', error);
        this.logout();
        return EMPTY;
      }),
      finalize(() => {
        if (lockApplication) {
          this.authStore.setProfileLoading(false);
        }
      })
    );
  }

  createAccount(request: CreateAccountRequest): Observable<unknown> {
    return this.authApi.createAccount(request).pipe(
      tap(() => this.loadProfileWithLogging('account creation'))
    );
  }

  joinAccount(request: JoinAccountRequest): Observable<unknown> {
    return this.authApi.joinAccount(request);
  }

  refreshTokens(): Observable<string> {
    return this.authStore.refreshAccessToken();
  }

  ensureAuthenticated(): Observable<boolean> {
    if (this.authStore.hasCompletedInitialLoad) {
      return of(true);
    }

    if (!this.authStore.isBrowserPlatform) {
      return of(false);
    }

    if (!this.authStore.token) {
      this.authStore.clearAuthState();
      return of(false);
    }

    return this.loadProfile().pipe(
      map(() => true),
      defaultIfEmpty(false),
      catchError(() => of(false))
    );
  }

  private handleAuthSideEffects(context: string, token: string, refreshToken?: string): void {
    this.authStore.setAuthData(token, refreshToken);
    this.loadProfileWithLogging(context);
  }

  private loadProfileWithLogging(context: string): void {
    if (!this.authStore.isBrowserPlatform) {
      return;
    }

    this.loadProfile().subscribe({
      error: (error) => console.error(`Failed to load profile after ${context}`, error)
    });
  }

}
